import { StandardEvents } from '@shopify/events';

/**
 * ECS (GLOSS-82): the buy box's Delivery row.
 *
 * Owns two jobs, both native-first:
 *
 * 1. Carrying the selected subscription plan into the cart. The row keeps a
 *    hidden `selling_plan` input associated to Shopify's own product form
 *    (via the input's `form` attribute). Selecting a plan enables the input
 *    with that plan's id; selecting "Once" disables it again, so a one-time
 *    purchase submits no plan at all — never an empty value.
 *
 * 2. Keeping the Total row honest. Plan prices are the variant's own
 *    selling plan allocations, formatted server-side into a JSON map at
 *    render time; this module only ever swaps those strings into the price
 *    element. Variant changes re-render the price natively (the theme's
 *    product-select event), so the plan price is re-applied after that
 *    morph settles.
 */
class PurchaseDelivery extends HTMLElement {
  /** @type {Record<string, { once: string, plans: Record<string, string> }>} */
  #prices = {};

  connectedCallback() {
    const data = this.querySelector('[data-purchase-delivery-prices]');
    try {
      this.#prices = JSON.parse(data?.textContent ?? '{}');
    } catch {
      this.#prices = {};
    }

    this.addEventListener('change', this.#onChange);

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @type {Element | null} */
  #section = null;

  get #form() {
    const id = this.dataset.formId;
    return id ? document.getElementById(id) : null;
  }

  get #variantId() {
    const form = this.#form;
    if (!(form instanceof HTMLFormElement)) return null;
    const input = form.querySelector('input[name="id"]');
    return input instanceof HTMLInputElement ? input.value : null;
  }

  get #selectedPlanId() {
    const checked = this.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement) || checked.value === '') return '';

    const frequency = this.querySelector('.purchase-delivery__frequency-select');
    if (frequency instanceof HTMLSelectElement && frequency.value) return frequency.value;

    return checked.value;
  }

  #onChange = () => {
    this.#apply();
  };

  /**
   * A variant change re-renders the price natively; re-apply the plan price
   * once that update has settled.
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #onProductSelect = (event) => {
    const settle = () => this.#apply();
    if (event.promise instanceof Promise) {
      event.promise.then(settle).catch(() => {});
    } else {
      requestAnimationFrame(settle);
    }
  };

  #apply() {
    const planId = this.#selectedPlanId;

    const frequency = this.querySelector('[data-purchase-delivery-frequency]');
    if (frequency instanceof HTMLElement) {
      frequency.hidden = planId === '';
    }

    const input = this.querySelector('[data-purchase-delivery-input]');
    if (input instanceof HTMLInputElement) {
      input.value = planId;
      input.disabled = planId === '';
    }

    const variant = this.#variantId ? this.#prices[this.#variantId] : null;
    if (!variant) return;

    const formatted = planId === '' ? variant.once : variant.plans[planId];
    if (!formatted) return;

    const priceElement = this.#section?.querySelector(
      'product-price [ref="priceContainer"] .price:not(.compare-at-price)'
    );
    if (!priceElement?.lastChild) return;

    priceElement.lastChild.textContent = formatted;
  }
}

if (!customElements.get('purchase-delivery')) {
  customElements.define('purchase-delivery', PurchaseDelivery);
}
